import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Emits `preconnect`/`dns-prefetch` hints for the origins the app actually
 * talks to, derived from `environment`.
 *
 * These used to be hardcoded in `index.html` to the *production* hosts. In
 * production they happened to be right; everywhere else all of them were dead —
 * Lighthouse on staging reported "Unused preconnect" three times while
 * `backstart.staging.memorabid.com`, the origin serving both the API and the
 * LCP image, got no hint at all.
 *
 * This runs during bootstrap, which on the server is before the document is
 * serialized, so the hints ship with the HTML — the only point at which a
 * preconnect is worth anything.
 */
@Injectable({ providedIn: 'root' })
export class ResourceHintsService {
  private readonly document = inject(DOCUMENT);

  add(): void {
    // A Set because in every environment except production the API and image
    // origins are the same host, and two identical preconnects are one wasted.
    const origins = new Set(
      [environment.APIBaseUrl, environment.ImageUrl]
        .map((url) => this.originOf(url))
        .filter((origin): origin is string => origin !== null)
    );

    for (const origin of origins) {
      this.appendHint('preconnect', origin);
      // Fallback for browsers that ignore preconnect.
      this.appendHint('dns-prefetch', origin);
    }
  }

  private originOf(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  private appendHint(rel: 'preconnect' | 'dns-prefetch', href: string): void {
    if (
      this.document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)
    ) {
      return;
    }

    const link = this.document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (rel === 'preconnect') {
      // Carried over verbatim from the markup this replaces. Note the hint only
      // warms a connection the browser can actually reuse, and the correct
      // value differs between the credentialed API calls and the plain <img>
      // loads — worth measuring in the LCP pass rather than guessing here.
      link.crossOrigin = '';
    }
    this.document.head.appendChild(link);
  }
}
